import { downloadXlsx } from "./exportExcel";
import { goalAchievement, itemAchievement } from "./scoreCalculator";
import { formatDate, formatDateTime } from "./format";

const blank = (value) => (value === null || value === undefined || value === "" ? "—" : value);
const num = (value) => (value === null || value === undefined || value === "" ? "—" : Number(value));

export function downloadEmployeeReport(review, { preparedBy } = {}) {
  const { employee = {}, cycle = {}, performance = {}, result = {}, config = {} } = review;
  const sa = review.selfAssessment;
  const ma = review.managerAssessment;
  const scores = result.frozen || performance;
  const weights = config.weights || {};

  const summaryRows = [
    ["Employee ID", employee.id],
    ["Name", employee.name],
    ["Designation", employee.designationName],
    ["Department", employee.departmentName],
    ["Reporting manager", employee.managerName],
    ["Performance cycle", cycle.name],
    ["Appraisal status", result.status],
    ["", ""],
    [`Goal score (weight ${blank(weights.goals)}%)`, num(scores.goalScore)],
    [`KRA score (weight ${blank(weights.kra)}%)`, num(scores.kraScore)],
    ["Final score", num(scores.finalScore)],
    ["Rating", blank(scores.rating)],
    ["", ""],
    ["Finalized by", blank(result.finalizedByName)],
    ["Finalized on", result.finalizedAt ? formatDateTime(result.finalizedAt) : "—"],
    ["HR comments", blank(result.hrComments)],
    ["Report prepared by", blank(preparedBy)],
    ["Report generated on", formatDateTime(new Date().toISOString())],
  ].map(([field, value]) => ({ field, value }));

  const goals = (review.goals || []).map((g) => ({
    ...g,
    achievementPct: goalAchievement(g),
  }));

  const assessmentRows = [
    ["Self assessment", "Status", sa ? `${sa.status}${sa.submittedAt ? ` on ${formatDate(sa.submittedAt)}` : ""}` : "Not started"],
    ["Self assessment", "Key achievements", sa?.keyAchievements],
    ["Self assessment", "Strengths", sa?.strengths],
    ["Self assessment", "Challenges", sa?.challenges],
    ["Self assessment", "Development areas", sa?.developmentAreas],
    ["Manager assessment", "Status", ma ? `${ma.managerName}, ${ma.status}${ma.submittedAt ? ` on ${formatDate(ma.submittedAt)}` : ""}` : "Not started"],
    ["Manager assessment", "Overall", ma?.comments],
    ["Manager assessment", "Strengths", ma?.strengths],
    ["Manager assessment", "Areas to improve", ma?.improvementAreas],
    ["Manager assessment", "Development plan", ma?.developmentPlan],
  ].map(([section, item, text]) => ({ section, item, text: blank(text) }));

  const title = `${employee.name} — ${cycle.name || "performance report"}`;

  downloadXlsx(`performance-report-${employee.id}-${employee.name}-${cycle.name || ""}`, [
    {
      name: "Summary",
      title,
      columns: [
        { key: "field", label: "Field", width: 30 },
        { key: "value", label: "Value", width: 44 },
      ],
      rows: summaryRows,
    },
    {
      name: "Goals",
      title: `Goals — ${employee.name}`,
      columns: [
        { key: "title", label: "Goal", width: 40 },
        { key: "categoryName", label: "Category" },
        { key: "source", label: "Source", value: (g) => g.source || "Employee proposed" },
        { key: "priority", label: "Priority" },
        { key: "weightage", label: "Weight %", value: (g) => num(g.weightage) },
        { key: "baseline", label: "Baseline", value: (g) => blank(g.baseline) },
        { key: "target", label: "Target", value: (g) => blank(g.target) },
        { key: "unit", label: "Unit" },
        { key: "dueDate", label: "Due date", value: (g) => (g.dueDate ? formatDate(g.dueDate) : "—") },
        { key: "achievement", label: "Self achievement %", value: (g) => num(g.achievement) },
        { key: "managerAchievement", label: "Manager achievement %", value: (g) => num(g.managerAchievement) },
        { key: "achievementPct", label: "Counted achievement %", value: (g) => num(g.achievementPct) },
        { key: "status", label: "Status" },
        { key: "managerComments", label: "Manager comments", value: (g) => blank(g.managerComments), width: 40 },
      ],
      rows: goals,
    },
    {
      name: "KRAs",
      title: `KRAs — ${employee.name}`,
      columns: [
        { key: "name", label: "KRA", width: 30 },
        { key: "measurementMethod", label: "Measured by", width: 40 },
        { key: "weightage", label: "Weight %", value: (r) => num(r.weightage) },
        { key: "target", label: "Target", value: (r) => num(r.target) },
        { key: "actual", label: "Actual", value: (r) => num(r.actual) },
        { key: "ach", label: "Achievement %", value: (r) => num(itemAchievement(r)) },
        { key: "status", label: "Status" },
      ],
      rows: review.kras || [],
    },
    {
      name: "KPIs",
      title: `KPIs — ${employee.name}`,
      columns: [
        { key: "name", label: "KPI", width: 30 },
        { key: "frequency", label: "Frequency" },
        { key: "unit", label: "Unit" },
        { key: "weightage", label: "Weight %", value: (r) => num(r.weightage) },
        { key: "target", label: "Target", value: (r) => num(r.target) },
        { key: "actual", label: "Actual", value: (r) => num(r.actual) },
        { key: "ach", label: "Achievement %", value: (r) => num(itemAchievement(r, r.direction)) },
        { key: "status", label: "Status" },
      ],
      rows: review.kpis || [],
    },
    {
      name: "Assessments",
      title: `Assessments — ${employee.name}`,
      columns: [
        { key: "section", label: "Section", width: 22 },
        { key: "item", label: "Item", width: 22 },
        { key: "text", label: "Comments", width: 80 },
      ],
      rows: assessmentRows,
    },
  ]);
}

export default downloadEmployeeReport;
